import { cellValue } from './demandListColumns.js';

const STORAGE_KEY = 'demandjet.kanbanCardFields';

/**
 * Fields that can appear on each Kanban card.
 * `debtorName` is always required as the card title.
 */
export const KANBAN_CARD_FIELDS = Object.freeze([
  {
    id: 'debtorName',
    label: 'Debtor',
    required: true,
    defaultVisible: true,
  },
  {
    id: 'demandId',
    label: 'Demand ID',
    defaultVisible: true,
  },
  {
    id: 'company',
    label: 'Company',
    defaultVisible: false,
  },
  {
    id: 'amount',
    label: 'Amount',
    defaultVisible: true,
  },
  {
    id: 'state',
    label: 'State',
    defaultVisible: true,
  },
  {
    id: 'city',
    label: 'City',
    defaultVisible: false,
  },
  {
    id: 'street',
    label: 'Street',
    defaultVisible: false,
  },
  {
    id: 'postal',
    label: 'Postal code',
    defaultVisible: false,
  },
  {
    id: 'email',
    label: 'Email',
    defaultVisible: false,
  },
  {
    id: 'attorney',
    label: 'Attorney partner',
    defaultVisible: false,
  },
  {
    id: 'invoiceId',
    label: 'Invoice ID',
    defaultVisible: false,
  },
  {
    id: 'tracking',
    label: 'Tracking #',
    defaultVisible: false,
  },
  {
    id: 'origin',
    label: 'Entry source',
    defaultVisible: false,
  },
  {
    id: 'installment',
    label: 'Payment plan',
    defaultVisible: false,
  },
  {
    id: 'dispute',
    label: 'Dispute type',
    defaultVisible: false,
  },
  {
    id: 'updatedAt',
    label: 'Last activity',
    defaultVisible: false,
  },
]);

export function defaultKanbanFieldIds() {
  return KANBAN_CARD_FIELDS.filter((field) => field.defaultVisible).map((field) => field.id);
}

export function loadKanbanFieldIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultKanbanFieldIds();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return defaultKanbanFieldIds();
    const allowed = new Set(KANBAN_CARD_FIELDS.map((field) => field.id));
    const next = parsed.filter((id) => allowed.has(id));
    if (!next.includes('debtorName')) next.unshift('debtorName');
    // Surface Demand ID on existing saved card layouts (users can hide it in fields).
    if (!next.includes('demandId')) {
      const insertAt = next.indexOf('debtorName') + 1;
      next.splice(insertAt, 0, 'demandId');
    }
    return next.length ? next : defaultKanbanFieldIds();
  } catch {
    return defaultKanbanFieldIds();
  }
}

export function saveKanbanFieldIds(ids) {
  const allowed = new Set(KANBAN_CARD_FIELDS.map((field) => field.id));
  const next = (ids || []).filter((id) => allowed.has(id));
  if (!next.includes('debtorName')) next.unshift('debtorName');
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
}

export function kanbanCardLines(matter, fieldIds) {
  const ids = (fieldIds || []).length ? fieldIds : defaultKanbanFieldIds();
  const titleId = ids.includes('debtorName') ? 'debtorName' : ids[0];
  const title = cellValue(matter, titleId);
  const details = ids
    .filter((id) => id !== titleId)
    .map((id) => ({
      id,
      label: KANBAN_CARD_FIELDS.find((field) => field.id === id)?.label || id,
      value: cellValue(matter, id),
    }))
    .filter((line) => line.value && line.value !== '—');

  // Keep a compact state · amount line when both are visible and adjacent-ish
  const stateIdx = details.findIndex((line) => line.id === 'state');
  const amountIdx = details.findIndex((line) => line.id === 'amount');
  if (stateIdx >= 0 && amountIdx >= 0) {
    const state = details[stateIdx];
    const amount = details[amountIdx];
    const combined = {
      id: 'stateAmount',
      label: 'State / Amount',
      value: `${state.value} · ${amount.value}`,
    };
    const without = details.filter((line) => line.id !== 'state' && line.id !== 'amount');
    const insertAt = Math.min(stateIdx, amountIdx);
    without.splice(insertAt, 0, combined);
    return { title, details: without };
  }

  return { title, details };
}
